/* eslint-disable no-underscore-dangle, no-undef */
/**
 * DeckOverlay — drop-in wrapper for deck.gl layers inside a @vis.gl/react-google-maps <Map>.
 *
 * Renders `layers` via GoogleMapsOverlay (raster-compatible; no vector Map ID required).
 *
 *   <DeckOverlay layers={[stopIconLayer]} style={{ zIndex: 2 }} />
 *
 * Pass a stable `style` object — it is applied once on attach.
 */
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { useMap } from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

import { createBlankRenderVerifier } from './blankRenderVerifier'

/**
 * luma.gl resizes the deck canvas from a device-pixel-content-box
 * ResizeObserver, and its setDrawingBufferSize assigns canvas.width/height
 * unconditionally — and any assignment to those attributes erases the drawing
 * buffer. At fractional devicePixelRatio (e.g. Windows 125%/150%) a
 * fractional-height map container makes the observed box oscillate ±1px as
 * raster draws reposition the canvas at subpixel offsets, wiping the buffer
 * up to once per frame mid-zoom — the markers visibly blink. Ignore ≤1px
 * deltas; real resizes still pass through.
 */
function guardDrawingBufferResize(deck) {
  const canvasContext = deck?.device?.canvasContext
  if (typeof canvasContext?.setDrawingBufferSize !== 'function' || canvasContext.setDrawingBufferSize.__resizeGuarded) return
  const originalSetSize = canvasContext.setDrawingBufferSize.bind(canvasContext)
  canvasContext.setDrawingBufferSize = (width, height) => {
    const within1px = Math.abs(width - canvasContext.drawingBufferWidth) <= 1 && Math.abs(height - canvasContext.drawingBufferHeight) <= 1
    if (!within1px) originalSetSize(width, height)
  }
  canvasContext.setDrawingBufferSize.__resizeGuarded = true
}

/**
 * Patches GoogleMapsOverlay to survive two races in @deck.gl/google-maps:
 *   1. draw() fires before the OverlayView projection is ready →
 *      `TypeError: Cannot read properties of null (reading 'fromLatLngToDivPixel')`.
 *      _onDrawRaster returns early; the useEffect polls and force-draws once ready.
 *   2. _onAdd runs after a rapid mount/unmount nulled this._map → deck.gl's
 *      createDeckInstance throws from utils.ts and leaves loading states stuck.
 *      Swallow it; tilesloaded/idle listeners will re-draw when the map settles.
 */
function createGuardedOverlay(interleaved, pane) {
  const overlay = new GoogleMapsOverlay({ interleaved })

  const originalOnDrawRaster = overlay._onDrawRaster
  overlay._onDrawRaster = function guardedOnDrawRaster() {
    if (!this._map || !this._overlay) return undefined
    const proj = this._overlay.getProjection?.()
    if (!proj) return undefined
    // Raster mode hard-codes the deck container into the overlayLayer pane
    // (z 101), which sits BELOW the pane Google Maps paints vector shapes
    // into (z ~106) — native polylines would draw over the deck markers.
    // Relocating the container to a higher pane (e.g. floatPane, z 107)
    // fixes the stacking; panes share one coordinate space, so the
    // absolute-positioned container needs no adjustment. Done here rather
    // than in _onAdd because deck creates its canvas lazily — by draw time
    // it exists. Re-checked each draw since GoogleMapsOverlay re-appends the
    // container to overlayLayer when renderingtype_changed reattaches it.
    if (pane && pane !== 'overlayLayer') {
      const panes = this._overlay.getPanes?.()
      const container = this._deck?.canvas?.parentElement
      if (panes?.[pane] && container && container.parentElement !== panes[pane]) {
        panes[pane].appendChild(container)
      }
    }
    // Re-checked each draw because deck creates its device lazily — by the
    // first few draws it exists, and the guard flag makes later calls no-ops.
    guardDrawingBufferResize(this._deck)
    return originalOnDrawRaster.call(this)
  }

  const originalOnAdd = overlay._onAdd
  overlay._onAdd = function guardedOnAdd() {
    if (!this._map) return undefined
    try {
      return originalOnAdd.call(this)
    } catch {
      return undefined
    }
  }

  return overlay
}

export function DeckOverlay({
  layers = [],
  interleaved = false,
  style,
  pane,
  verifyRender = false,
  componentName = 'DeckOverlay',
  overlayInstanceRef,
}) {
  const map = useMap()

  // Holds the active overlay so the layers effect can push updates.
  // Nulled out in cleanup so the layers effect is a safe no-op during teardown.
  const overlayRef = useRef(null)

  // Gates the layers effect: first run force-draws to beat the pre-projection race,
  // subsequent runs defer to Google Maps' frame scheduler. Sync force-draws on every
  // layer-prop change during pan/zoom interrupt the map's animation frame and flicker.
  const hasInitialDrawRef = useRef(false)

  // Attach / detach the overlay from the map instance
  useEffect(() => {
    if (!map) return undefined

    // Fresh overlay per mount — reusing across StrictMode's cleanup→remount cycle
    // causes GoogleMapsOverlay to double-register its `renderingtype_changed`
    // listener (setMap(null) doesn't remove it), and the second _createOverlay
    // overwrites this._overlay before the first onAdd runs, leaving the deck
    // canvas disconnected from the DOM.
    const overlay = createGuardedOverlay(interleaved, pane)
    overlayRef.current = overlay
    // eslint-disable-next-line no-param-reassign
    if (overlayInstanceRef) overlayInstanceRef.current = overlay
    hasInitialDrawRef.current = false

    // Set style before setMap so GoogleMapsOverlay picks it up when it creates
    // the deck canvas container.
    if (style) overlay.setProps({ style })

    // Post-render canary for GPUs that pass the probes but composite blank
    // frames (CS-2844) — set before setMap so deck picks it up on creation.
    if (verifyRender) {
      overlay.setProps({
        onAfterRender: createBlankRenderVerifier({ componentName, getDeck: () => overlayRef.current?._deck }),
      })
    }

    overlay.setMap(map)

    // _onRemove sets layerFilter to hide-all on setMap(null); reset it so
    // layers are visible on fresh attach.
    overlay.setProps({ layerFilter: null })

    // Initial draw() fires before the OverlayView projection is ready, so the
    // guard skips it and the deck sits at default viewport (0,0, zoom 1).
    // Poll until deck+map+projection are ready, then force the real draw.
    // Raster path: OverlayView has getProjection() → _onDrawRaster directly.
    // Vector path: WebGLOverlayView → requestRedraw() so Google Maps fires _onDrawVector.
    let cancelled = false
    const scheduleRedraw = delay => {
      setTimeout(() => {
        if (cancelled) return
        const ol = overlay
        if (!ol._deck || !ol._map) {
          scheduleRedraw(Math.min(delay * 2, 800))
          return
        }
        // getBounds() null → projection not ready; drawing now would place
        // markers at world-position (0,0), off-screen.
        if (!ol._map.getBounds?.()) {
          scheduleRedraw(Math.min(delay * 2, 800))
          return
        }
        const proj = ol._overlay?.getProjection?.()
        if (proj) {
          ol._onDrawRaster.call(ol)
        } else if (typeof ol._overlay?.requestRedraw === 'function') {
          ol._overlay.requestRedraw()
        } else {
          scheduleRedraw(Math.min(delay * 2, 800))
        }
      }, delay)
    }
    scheduleRedraw(50)

    // Safety net for the cache-hit race: data arrives before getBounds() is
    // available, the layers-effect poll expires, and idle has already fired with
    // empty layers. tilesloaded fires earlier than idle; idle catches pan/zoom end.
    const forceDraw = () => {
      if (cancelled) return
      const ol = overlay
      if (!ol._deck || !ol._map) return
      if (ol._overlay?.getProjection?.()) {
        ol._onDrawRaster.call(ol)
      } else if (typeof ol._overlay?.requestRedraw === 'function') {
        ol._overlay.requestRedraw()
      }
    }
    const tilesListener = map.addListener('tilesloaded', forceDraw)
    const idleListener = map.addListener('idle', forceDraw)

    return () => {
      cancelled = true
      google.maps.event.removeListener(tilesListener)
      google.maps.event.removeListener(idleListener)
      overlayRef.current = null
      // eslint-disable-next-line no-param-reassign
      if (overlayInstanceRef) overlayInstanceRef.current = null
      // finalize() kills the deck + inner overlay so stale
      // renderingtype_changed listeners can't fire after cleanup.
      overlay.finalize()
    }
    // style/interleaved/pane/verifyRender/componentName are constructor-time —
    // changing them requires re-attach (no consumer toggles them after mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  // Push updated layers to the overlay. The first time, force an immediate draw
  // to beat the pre-projection race; after that, let Google Maps schedule the
  // redraw on its next frame so pan/zoom doesn't flicker.
  useEffect(() => {
    const overlay = overlayRef.current
    if (!overlay) return undefined

    overlay.setProps({ layers })

    if (hasInitialDrawRef.current) {
      overlay._overlay?.changed?.()
      return undefined
    }

    const draw = () => {
      if (!overlay._deck || !overlay._map) return false
      if (!overlay._map.getBounds?.()) return false
      const proj = overlay._overlay?.getProjection?.()
      if (proj) {
        overlay._onDrawRaster.call(overlay)
        hasInitialDrawRef.current = true
        return true
      }
      overlay._overlay?.changed?.()
      return false
    }

    if (!draw()) {
      // Poll every 50 ms (max 600 ms); fall back to the next `idle` event
      // which guarantees the projection is ready.
      let attempts = 0
      let idleListener = null
      const poll = setInterval(() => {
        attempts += 1
        if (draw()) {
          clearInterval(poll)
        } else if (attempts >= 12) {
          clearInterval(poll)
          if (overlay._map) {
            idleListener = overlay._map.addListener('idle', () => {
              google.maps.event.removeListener(idleListener)
              idleListener = null
              draw()
            })
          }
        }
      }, 50)
      return () => {
        clearInterval(poll)
        if (idleListener) google.maps.event.removeListener(idleListener)
      }
    }
    return undefined
    // `map` is in deps so this effect re-runs once the mount effect creates the
    // overlay — otherwise it fires once with overlayRef.current=null and never retries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, map])

  return null
}

DeckOverlay.propTypes = {
  layers: PropTypes.array,
  interleaved: PropTypes.bool,
  style: PropTypes.object,
  // OverlayView pane to host the deck container ('floatPane' stacks markers
  // above native polylines); omit for deck's default overlayLayer.
  pane: PropTypes.oneOf(['floatPane', 'overlayMouseTarget', 'overlayLayer']),
  // Opt-in post-render blank-frame detection (see blankRenderVerifier.js).
  // Constructor-time, like style/interleaved/pane — not togglable after mount.
  verifyRender: PropTypes.bool,
  // Breadcrumb for the fatal-flag warn when the verifier trips.
  componentName: PropTypes.string,
  // Receives the live GoogleMapsOverlay instance (e.g. for pickObject) — nulled on unmount.
  overlayInstanceRef: PropTypes.shape({ current: PropTypes.any }),
}
